import { OverflowMenu, OverflowMenuItem } from "@carbon/react";
import { useModalWithData } from "@flaminem/ui-components";
import useAxios from "axios-hooks";
import { saveAs } from "file-saver";
import { generatePath, useNavigate } from "react-router-dom";

import { AuthorizedOverflowMenuItem } from "src/authentication/AuthorizedMenu";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { usePermissions } from "src/authentication/usePermissions";
import { ModalTransition } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer";
import { CreateScenarioVersionModal } from "src/features/scenario/CreateVersion/CreateScenarioVersionModal";
import { ScenarioVersion } from "src/http/ScenarioVersion.api";
import { useT } from "src/internationalization";
import { ShortScenario } from "src/types/scenario";

export function ScenarioActionsMenu({ scenario }: { scenario: ShortScenario }) {
  const t = useT();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const {
    close: closeCreateVersionModal,
    isOpen: isCreateVersionModalOpen,
    open: openCreateVersionModal,
    data: selectedScenarioForVersion,
  } = useModalWithData<ShortScenario | null>(null);

  const { showSuccessToast, showErrorToast } = useToast();

  const [, execute] = useAxios(
    {
      method: "GET",
    },
    { manual: true }
  );

  const exportScenarioVersion = async (id: string, name: string) => {
    try {
      const response = await execute({
        url: ScenarioVersion.ScenarioVersion().getScenarioVersionById(id),
      });
      if (response.data && response.status === 200) {
        const jsonBlob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: "application/json",
        });
        saveAs(jsonBlob, name);
        showSuccessToast(t("page.scenarios.tab.requests.export.toast.success.message"));
      }
    } catch (e) {
      showErrorToast(t("page.scenarios.tab.requests.export.toast.error.message"));
    }
  };

  return (
    <>
      <OverflowMenu flipped={true} onClick={(e) => e.stopPropagation()}>
        <OverflowMenuItem
          itemText={t("page.scenarios.tab.requests.table.menu.detail")}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              generatePath("/scenario/:caseTypeCode/:id/active", {
                caseTypeCode: scenario.caseType.code,
                id: scenario.id,
              })
            );
          }}
        />
        {!scenario.hasDraftVersion &&
          hasPermission(ScopeResource.SCENARIO, Scope.CREATE, Scope.EDIT) && (
            <OverflowMenuItem
              itemText={t("scenarios.action.create.new.version")}
              onClick={(e) => {
                openCreateVersionModal()(scenario);
                e.stopPropagation();
              }}
            />
          )}

        <AuthorizedOverflowMenuItem
          requiredPermissions={[{ resource: ScopeResource.SCENARIO, scopes: [Scope.EXPORT] }]}
          itemText={t("page.scenarios.tab.requests.table.menu.export")}
          onClick={async (e) => {
            e.stopPropagation();
            await exportScenarioVersion(scenario.activeVersion.id, scenario.name);
          }}
        />
      </OverflowMenu>
      <ModalTransition>
        {isCreateVersionModalOpen && selectedScenarioForVersion && (
          <CreateScenarioVersionModal
            onConfirm={closeCreateVersionModal()}
            scenario={selectedScenarioForVersion}
            onClose={closeCreateVersionModal()}
          />
        )}
      </ModalTransition>
    </>
  );
}
